import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import api from "../../../../../utils/axios";
import LocationModal from "../../LocationModal";
import { getData } from "../../../../../utils/SamuiUtils";

export default function ServiceLocationModal({ isOpen, onClose, arId, onSelect }) {
  const [locations, setLocations] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [dataReady, setDataReady] = useState(false); // Control when to show modal content

  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [editLocation, setEditLocation] = useState(null);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [servicePurposes, setServicePurposes] = useState([]);

  useEffect(() => {
    if (isOpen && arId) {
      setDataReady(false); // Hide modal content until data is ready
      setLoading(true);
      setLocations([]); // Reset locations immediately when opening
      setSearch(""); // Reset search when opening
      
      const startTime = Date.now();
      
      api
        .get(`/get-tb-set-service-addr/${arId}`)
        .then((res) => {
          if (res.data.status === "OK") {
            setLocations(res.data.result?.recordset || []);
          } else {
            setLocations([]);
          }
        })
        .catch((err) => {
          console.error("โหลดสถานที่บริการล้มเหลว:", err);
          setLocations([]);
        })
        .finally(() => {
          setLoading(false);
          // Ensure minimum 2 seconds loading time
          const elapsedTime = Date.now() - startTime;
          const remainingTime = Math.max(0, 2000 - elapsedTime);
          
          setTimeout(() => {
            setDataReady(true);
          }, remainingTime);
        });
    } else if (!isOpen) {
      // Reset state when modal closes
      setLocations([]);
      setSearch("");
      setLoading(false);
      setDataReady(false);
      setLocationModalOpen(false);
      setEditLocation(null);
    }
  }, [isOpen, arId]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchServiceTypes = async () => {
      try {
        const res = await getData(
          "Tb_Set_ServiceAddrType",
          "ServiceAddr_Type_Status = 'Y'"
        );
        const rows = Array.isArray(res) ? res : res?.data || [];
        const mapped = rows
          .map((item) => ({
            id: item?.ServiceAddr_Type_Id ?? item?.id ?? item?.Id,
            name:
              item?.ServiceAddr_Type_Name ??
              item?.name ??
              item?.ServiceAddr_Type,
          }))
          .filter((item) => item.id && item.name);
        setServiceTypes(mapped);
      } catch (err) {
        console.error("โหลดประเภทสถานที่บริการไม่สำเร็จ:", err);
        setServiceTypes([]);
      }
    };
    fetchServiceTypes();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchServicePurposes = async () => {
      try {
        const res = await getData(
          "Tb_Set_ServicePurpose",
          "ServicePurpose_Status = 'Y'"
        );
        const rows = Array.isArray(res) ? res : res?.data || [];
        const mapped = rows
          .map((item) => ({
            id:
              item?.ServicePurpose_Id ??
              item?.ServicePurposeId ??
              item?.Purpose_Id ??
              item?.id ??
              item?.Id,
            name:
              item?.ServicePurpose_Name ??
              item?.Purpose_Name ??
              item?.name ??
              item?.ServicePurpose,
          }))
          .filter((item) => item.id && item.name);
        setServicePurposes(mapped);
      } catch (err) {
        console.error("โหลดวัตถุประสงค์ไม่สำเร็จ:", err);
        setServicePurposes([]);
      }
    };
    fetchServicePurposes();
  }, [isOpen]);

  if (!isOpen) return null;

  const formatAddress = (loc) => {
    return [
      loc.AddrNo ? `เลขที่ ${loc.AddrNo}` : "",
      loc.Building ? `อาคาร ${loc.Building}` : "",
      loc.Village ? `หมู่บ้าน ${loc.Village}` : "",
      loc.Moo ? `ม.${loc.Moo}` : "",
      loc.Soi ? `ซ.${loc.Soi}` : "",
      loc.Road ? `ถ.${loc.Road}` : "",
      loc.SubDistrictName ? `ต.${loc.SubDistrictName}` : "",
      loc.DistrictName ? `อ.${loc.DistrictName}` : "",
      loc.ProvinceName ? `จ.${loc.ProvinceName}` : "",
      loc.Zipcode || "",
    ]
      .filter(Boolean)
      .join(" ");
  };

  const filtered = locations.filter((loc) =>
    formatAddress(loc).toLowerCase().includes(search.toLowerCase())
  );

  const purposeMap = useMemo(() => {
    const map = new Map();
    servicePurposes.forEach((item) => {
      map.set(String(item.id), item.name);
    });
    return map;
  }, [servicePurposes]);


  return (
    <>
      <div className={`modal fade show`} style={{ display: "block" }} tabIndex="-1">
        <div
          className="modal-dialog modal-dialog-centered"
          style={{
            maxWidth: "1100px",
            width: "95%",
          }}
        >
          <div className="modal-content border-0 shadow-lg">

            {/*  CSS override */}
            <style>
              {`
                .service-location-table {
                  width: 100% !important;
                  min-width: unset !important;
                  table-layout: auto !important;
                }
                .service-location-table th,
                .service-location-table td {
                  white-space: normal !important;
                  word-break: break-word !important;
                  text-align: left !important;
                  font-size: 14px !important;
                  padding: 6px 8px !important;
                }
                .service-location-table thead th {
                  background-color: #E13F57 !important;
                  color: white !important;
                  font-weight: 600 !important;
                  position: sticky;
                  top: 0;
                  z-index: 5;
                }
              `}
            </style>

            <div className="modal-header" style={{ backgroundColor: "#E13F57", color: "white" }}>
              <h5 className="modal-title" style={{ fontWeight: "bold" }}>เลือกสถานที่บริการ</h5>
              <button type="button" className="close" onClick={onClose}>
                <span>&times;</span>
              </button>
            </div>

            <div className="modal-body">
              {!dataReady ? (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status" style={{ color: "#E13F57", width: "3rem", height: "3rem" }}>
                      <span className="sr-only">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">กำลังโหลดข้อมูล...</p>
                  </div>
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    className="form-control mb-3"
                    placeholder="ค้นหาสถานที่บริการ..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ height: "36px", fontSize: "14px" }}
                  />

                  {/*  ไม่มี scroll ซ้าย-ขวา */}
                  <div
                    className="table-responsive"
                    style={{ maxHeight: "350px", overflowY: "auto", overflowX: "hidden" }}
                  >
                    <table className="table table-striped table-hover mb-0 service-location-table">

                    <thead>
                      <tr>
                        <th style={{ width: "12%", textAlign: "center" }}>วัตถุประสงค์</th>
                        <th style={{ width: "22%" }}>ชื่อสถานที่บริการ</th>
                        <th style={{ width: "14%", textAlign: "center" }}>จังหวัด</th>
                        <th style={{ width: "14%", textAlign: "center" }}>เบอร์โทร</th>
                        <th style={{ width: "36%" }}>ที่อยู่</th>
                        <th style={{ width: "14%", textAlign: "center" }}>การจัดการ</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filtered.length > 0 ? (
                        filtered.map((loc) => (
                          <tr key={loc.ServiceAddr_Id}>
                            <td style={{ textAlign: "center" }}>
                              {purposeMap.get(String(loc.ServicePurpose)) || "-"}
                            </td>
                            <td>{loc.ServiceAddr_Name || "-"}</td>
                            <td style={{ textAlign: "center" }}>{loc.ProvinceName || "-"}</td>
                            <td style={{ textAlign: "center" }}>{loc.AR_Tel || "-"}</td>
                            <td>{formatAddress(loc)}</td>
                            <td style={{ textAlign: "center" }}>
                              <div className="d-flex justify-content-center gap-2">
                                <button
                                  className="btn btn-sm"
                                  style={{ backgroundColor: "#F17F25", color: "white", width: "70px", fontSize: "14px" }}
                                  onClick={() => {
                                    onSelect(
                                      {
                                        ServiceAddr_Id: loc.ServiceAddr_Id,
                                        ServiceAddr_Name: loc.ServiceAddr_Name,
                                        ServiceAddr_FullName: formatAddress(loc),
                                        AR_Id: arId,
                                      },
                                      true
                                    );
                                    onClose();
                                  }}
                                >
                                  เลือก
                                </button>

                                <button
                                  className="btn btn-sm btn-warning"
                                  style={{ width: "70px", color: "white", fontSize: "14px" }}
                                  onClick={() => {
                                    setEditLocation(loc);
                                    setLocationModalOpen(true);
                                  }}
                                >
                                  แก้ไข
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="text-center py-3 text-muted">
                            ไม่พบข้อมูล
                          </td>
                        </tr>
                      )}
                    </tbody>

                    </table>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="btn"
                style={{ backgroundColor: "#E13F57", color: "white", borderColor: "#E13F57" }}
                onClick={onClose}
              >
                ปิด
              </button>
            </div>

          </div>
        </div>
      </div>

      {/*  Backdrop */}
      {isOpen && (
        <div className="modal-backdrop fade show"></div>
      )}


      {/*  Modal แก้ไขสถานที่ */}
      {locationModalOpen && (
        <LocationModal
          isOpen={locationModalOpen}
          onClose={() => setLocationModalOpen(false)}
          ServiceAddr_Id={editLocation?.ServiceAddr_Id || null}
          serviceTypes={serviceTypes}
          onSave={async (formData) => {
            try {
              let res;
              if (formData.ServiceAddr_Id) {
                res = await api.put(`/update-tb-set-service-addr/${formData.ServiceAddr_Id}`, formData);
              } else {
                res = await api.post(`/create-tb-set-service-addr`, formData);
              }

              if (res.data.status === "OK") {
                const refreshed = await api.get(`/get-tb-set-service-addr/${arId}`);
                if (refreshed.data.status === "OK") {
                  setLocations(refreshed.data.result?.recordset || []);
                }

                setLocationModalOpen(false);

                await onSelect(
                  {
                    ServiceAddr_Id: formData.ServiceAddr_Id || res.data.ServiceAddr_Id,
                    ServiceAddr_Name: formData.ServiceAddr_Name,
                    ServiceAddr_FullName: formatAddress(formData),
                    AR_Id: arId,
                  },
                  true
                );

                return { status: "OK" };
              }

              return { status: "FAILED" };

            } catch {
              Swal.fire("ผิดพลาด", "ไม่สามารถบันทึกข้อมูลได้", "error");
              return { status: "FAILED" };
            }
          }}
        />
      )}
    </>
  );
}
